import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from './api.service';
import {
  LiviaChatRequest,
  LiviaChatResponse,
  LiviaThreadMessage
} from '../models/livia.model';
import { ROLES } from '../constants/roles.constants';

/** Aligné sur `rag/roleAccess.js` (backend). */
export const LIVIA_ALLOWED_ROLES: readonly string[] = [
  ROLES.SUPERADMIN,
  ROLES.ADMIN,
  ROLES.EHPAD,
  ROLES.ANIMATEUR,
  ROLES.PRATICIEN,
  ROLES.TECH,
  ROLES.FAMILLE,
  ROLES.PODOLOGUE,
  ROLES.COIFFEUR,
  ROLES.KINE
];

/** Session locale (pas d’API d’historique Livia pour l’instant). */
export interface LiviaStoredSession {
  conversationId: string | null;
  messages: LiviaThreadMessage[];
}

@Injectable({
  providedIn: 'root'
})
export class LiviaService {
  private readonly endpoint = '/api/livia';
  /** Limite pour éviter de saturer localStorage. */
  private static readonly MAX_STORED_MESSAGES = 80;

  constructor(private apiService: ApiService) {}

  isRoleAllowed(roleName?: string | null): boolean {
    const role = String(roleName || '')
      .trim()
      .toLowerCase();
    return LIVIA_ALLOWED_ROLES.includes(role);
  }

  chat(payload: LiviaChatRequest): Observable<LiviaChatResponse> {
    return this.apiService.post<LiviaChatResponse>(`${this.endpoint}/chat`, {
      message: payload.message,
      ...(payload.conversationId ? { conversationId: payload.conversationId } : {})
    });
  }

  private sessionKey(userId: number): string {
    return `livia.session.${userId}`;
  }

  /** Ancienne clé (id seul) — migrée à la lecture. */
  private legacyIdKey(userId: number): string {
    return `livia.conversationId.${userId}`;
  }

  getStoredSession(userId: number): LiviaStoredSession {
    try {
      const raw = localStorage.getItem(this.sessionKey(userId));
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<LiviaStoredSession>;
        const messages = Array.isArray(parsed.messages)
          ? parsed.messages.filter(
              (m): m is LiviaThreadMessage =>
                !!m &&
                typeof m.id === 'string' &&
                (m.role === 'user' || m.role === 'assistant') &&
                typeof m.content === 'string' &&
                typeof m.at === 'string'
            )
          : [];
        return {
          conversationId:
            typeof parsed.conversationId === 'string' && parsed.conversationId
              ? parsed.conversationId
              : null,
          messages
        };
      }

      const legacyId = localStorage.getItem(this.legacyIdKey(userId));
      if (legacyId) {
        return { conversationId: legacyId, messages: [] };
      }
    } catch {
      /* ignore quota / private mode / JSON invalide */
    }
    return { conversationId: null, messages: [] };
  }

  getStoredConversationId(userId: number): string | null {
    return this.getStoredSession(userId).conversationId;
  }

  saveSession(
    userId: number,
    conversationId: string | null,
    messages: LiviaThreadMessage[]
  ): void {
    try {
      const trimmed =
        messages.length > LiviaService.MAX_STORED_MESSAGES
          ? messages.slice(-LiviaService.MAX_STORED_MESSAGES)
          : messages;
      const session: LiviaStoredSession = {
        conversationId,
        messages: trimmed
      };
      localStorage.setItem(this.sessionKey(userId), JSON.stringify(session));
      localStorage.removeItem(this.legacyIdKey(userId));
    } catch {
      /* ignore quota / private mode */
    }
  }

  setStoredConversationId(userId: number, conversationId: string): void {
    const current = this.getStoredSession(userId);
    this.saveSession(userId, conversationId, current.messages);
  }

  clearStoredSession(userId: number): void {
    try {
      localStorage.removeItem(this.sessionKey(userId));
      localStorage.removeItem(this.legacyIdKey(userId));
    } catch {
      /* ignore */
    }
  }

  /** @deprecated préférer clearStoredSession */
  clearStoredConversationId(userId: number): void {
    this.clearStoredSession(userId);
  }

  private fabDismissKey(userId: number): string {
    return `livia.fab.dismissed.${userId}`;
  }

  isFabDismissed(userId: number): boolean {
    try {
      return localStorage.getItem(this.fabDismissKey(userId)) === '1';
    } catch {
      return false;
    }
  }

  setFabDismissed(userId: number, dismissed: boolean): void {
    try {
      if (dismissed) {
        localStorage.setItem(this.fabDismissKey(userId), '1');
      } else {
        localStorage.removeItem(this.fabDismissKey(userId));
      }
    } catch {
      /* ignore */
    }
  }

  /** Demande d’ouvrir Livia au prochain affichage de la page Messages (sans query URL). */
  private openOnNextMessagesVisit = false;

  requestOpenOnMessages(): void {
    this.openOnNextMessagesVisit = true;
  }

  consumeOpenOnMessages(): boolean {
    const open = this.openOnNextMessagesVisit;
    this.openOnNextMessagesVisit = false;
    return open;
  }

  /**
   * Route d’entrée Livia selon le rôle :
   * - famille / gestionnaire / animateur → messagerie
   * - autres rôles autorisés → `/livia` (assistant seul)
   */
  getEntryPath(roleName?: string | null): string {
    const role = String(roleName || '')
      .trim()
      .toLowerCase();
    if (role === ROLES.FAMILLE) {
      return '/messages/famille';
    }
    if (role === ROLES.EHPAD || role === ROLES.ANIMATEUR) {
      return '/messages/list';
    }
    return '/livia';
  }
}
